import {removeDuplicatesFromArray} from "helpers/filterHandlers";
import {flatMap} from "lodash";
import {createSelector} from "reselect";
import {
	countryOptionsPreCalculatedSelector,
	countryOptionsSelector,
} from "store/search/searchSelectors";
import {targetListCompaniesSelector} from "store/target-list/targetListSelectors";

export const activeAccordionOnFilterSelector = (state) => state.filter.activeAccordionOnFilter;

export const selectedCandidatesSelector = (state) => state.filter.selectedCandidates;

export const selectedCandidatesInProgressSelector = (state) =>
	state.filter.selectedCandidatesInProgress;

export const searchedCandidatesInProgressSelector = (state) =>
	state.filter.searchedCandidatesInProgress;

export const searchedCompaniesInProgressSelector = (state) =>
	state.filter.searchedCompaniesInProgress;

export const aiCompanyIdeaFilterModeSelector = (state) => state.filter.ai_company_idea_filter_mode;

export const candidateJobTitleFilterModeSelector = (state) => state.filter.job_title_filter_mode;
export const candidateSelectedJobTitlesSelector = (state) =>
	state.filter.candidate_filters.current_job_titles_or || [];
export const candidateLinkedInUrlSelector = (state) => state.filter.candidate_filters.linkedin_url;

export const candidateCountriesSelector = (state) => state.filter.candidate_filters.countries;

export const candidateCountryRegionsSelector = (state) => state.filter.candidate_filters.regions;
export const genderDiversityImportanceSelector = (state) =>
	state.filter.candidate_filters.gender_diversity_importance;
export const ethnicDiversityImportanceSelector = (state) =>
	state.filter.candidate_filters.ethnic_diversity_importance;

export const companyCountriesSelector = (state) => state.filter.company_filters.countries;

export const companyHQCountriesSelector = (state) =>
	state.filter.company_filters.headquarters_countries;

export const specialtySearchQuerySelector = (state) => state.filter.specialtySearchQuery;

export const selectedCompaniesSelector = (state) => state.filter.selected_companies || [];

export const searchedCompaniesSelector = (state) => state.filter.searchedCompanies?.results || [];
export const searchedCompaniesPaginationSelector = (state) =>
	state.filter.searchedCompanies?.pagination || [];

export const anyCompaniesSelector = (state) => state.filter.candidate_filters.companies;

export const currentCompaniesSelector = (state) => state.filter.candidate_filters.current_companies;

export const previousCompaniesSelector = (state) =>
	state.filter.candidate_filters.previous_companies;

export const candidateAnyTLCompaniesSelector = (state) =>
	state.filter.candidate_filters.tl_companies;

export const candidateCurrentTLCompaniesSelector = (state) =>
	state.filter.candidate_filters.current_tl_companies;

export const candidatePreviousTLCompaniesSelector = (state) =>
	state.filter.candidate_filters.previous_tl_companies;

export const companyIndustriesSelector = (state) => state.filter.company_filters.industries;

export const currentSenioritySelector = (state) => state.filter.candidate_filters.current_seniority;

export const candidateLanguagesSelector = (state) => state.filter.candidate_filters.languages;

export const companySizesSelector = (state) => state.filter.company_filters.size;
export const companyRevenuesSelector = (state) => state.filter.company_filters.revenue;

export const companyIndexesSelector = (state) => state.filter.company_filters.indexes;

export const candidateCompanyPositionSelector = (state) =>
	state.filter.candidate_filters_position.companies;

export const candidateTargetListToggleSelector = (state) =>
	state.filter.candidate_filters_position.target_list_toggle;

export const candidateTargetListPositionSelector = (state) =>
	state.filter.candidate_filters_position.target_list;

export const candidateTLCompaniesByPositionSelector = createSelector(
	[
		candidateCurrentTLCompaniesSelector,
		candidatePreviousTLCompaniesSelector,
		candidateAnyTLCompaniesSelector,
		(_, position) => position,
	],
	(currentTLCompanies, previousTLCompanies, anyTLCompanies, position) => {
		switch (position) {
			case "current":
				return currentTLCompanies;
			case "previous":
				return previousTLCompanies;
			case "any":
				return anyTLCompanies;
			default:
				return [];
		}
	},
);

export const showEnhancedCandidatesSelector = (state) => state.filter.enhanceCandidatesResult;

export const candidateFiltersSelector = (state) => state.filter.candidate_filters;

export const searchedCandidateIdsFilterSelector = (state) =>
	state.filter.candidate_filters.candidates;

export const showEnhancedCompaniesSelector = (state) => state.filter.enhanceCompaniesResult;

export const companyFiltersSelector = (state) => state.filter.company_filters;

export const allCompaniesCountSelector = createSelector(
	[currentCompaniesSelector, previousCompaniesSelector, anyCompaniesSelector],
	(current, previous, any) => current?.length + previous?.length + any?.length,
);

export const targetListCompaniesByPositionSelector = createSelector(
	[
		candidateCurrentTLCompaniesSelector,
		candidatePreviousTLCompaniesSelector,
		candidateAnyTLCompaniesSelector,
		(_, position) => position,
	],
	(currentTLCompanies, previousTLCompanies, anyTLCompanies, position) => {
		switch (position) {
			case "current":
				return currentTLCompanies;
			case "previous":
				return previousTLCompanies;
			case "any":
				return anyTLCompanies;
			default:
				return [];
		}
	},
);

export const companiesByPositionSelector = createSelector(
	[
		currentCompaniesSelector,
		previousCompaniesSelector,
		anyCompaniesSelector,
		candidateCompanyPositionSelector,
		(_, position) => position,
	],
	(currentCompanies, previousCompanies, anyCompanies, position, manuallyDefinedPosition) => {
		switch (manuallyDefinedPosition || position) {
			case "current":
				return currentCompanies;
			case "previous":
				return previousCompanies;
			case "any":
				return anyCompanies;
			default:
				return [];
		}
	},
);

export const tlCompanyIdsByTargetListPositionSelector = createSelector(
	[
		candidateCurrentTLCompaniesSelector,
		candidatePreviousTLCompaniesSelector,
		candidateAnyTLCompaniesSelector,
		candidateTargetListPositionSelector,
	],
	(current, previous, any, position) => {
		switch (position) {
			case "current":
				return current;
			case "previous":
				return previous;
			case "any":
				return any;
			default:
				return [];
		}
	},
);

export const companiesFilterKeyByPositionSelector = createSelector(
	(_, position) => position,
	(position) => {
		switch (position) {
			case "current":
				return "current_companies";
			case "previous":
				return "previous_companies";
			case "any":
			default:
				return "companies";
		}
	},
);

export const tlCompaniesFilterKeyByPositionSelector = createSelector(
	(_, position) => position,
	(position) => {
		switch (position) {
			case "current":
				return "current_tl_companies";
			case "previous":
				return "previous_tl_companies";
			case "any":
			default:
				return "tl_companies";
		}
	},
);

export const isTargetListExpandedSelector = createSelector(
	[
		targetListCompaniesSelector,
		candidateCurrentTLCompaniesSelector,
		candidatePreviousTLCompaniesSelector,
		candidateAnyTLCompaniesSelector,
	],
	(targetListCompanies, current, previous, any) => {
		const companies = [...current, ...previous, ...any];

		return targetListCompanies.length > 0 && companies?.length > 0;
	},
);

export const targetListFilterCountSelector = createSelector(
	[
		candidateCurrentTLCompaniesSelector,
		candidatePreviousTLCompaniesSelector,
		candidateAnyTLCompaniesSelector,
	],
	(current, previous, any) => current.length + previous.length + any.length,
);

export const selectedTargetListCompaniesSelector = createSelector(
	tlCompanyIdsByTargetListPositionSelector,
	(targetListCompanies) => new Set(targetListCompanies.map((c) => c.id)),
);

export const flattenCountriesSelector = createSelector(
	[countryOptionsSelector],
	(continentCountries) => flatMap(continentCountries, "countries"),
);

// Pre-calculated countries include the full hierarchy: regions -> states -> cities
export const flattenCountriesPreCalculatedSelector = createSelector(
	[countryOptionsPreCalculatedSelector],
	(continentCountries) => flatMap(continentCountries, "countries"),
);

export const filterConnectivityLogicOptionsSelector = createSelector([], () => {
	return [
		{label: "AND", value: "and"},
		{label: "OR", value: "or"},
		{label: "NOT", value: "not"},
	];
});

export const companyHierarchyOptionsSelector = () => [
	{label: "Only", value: "no_hierarchy"},
	{label: "Subsidiaries", value: "subsidiaries"},
	{label: "Group", value: "group"},
];

export const filterPositionOptionsSelector = createSelector(
	(state) => state,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	(_) => {
		return [
			{label: "Current", value: "current"},
			{label: "Previous", value: "previous"},
			{label: "Any", value: "any"},
		];
	},
);

// COMPANY SPECIALITY LOGIC SELECTION

export const specialitiesSelector = (state) => state.filter.company_filters.specialties;

export const ignoreTLCompaniesOnFilterSelector = (state) => state.filter.ignoreTLCompaniesOnFilter;

export const companySpecialitiesConnectivityLogicSelector = (state) =>
	state.filter.company_speciality_selected_connectivity;

export const companySearchRadiusSelector = (state) => state.filter.company_search_radius;

export const companyModeResultsSelector = (state) => state.filter.company_mode_results;

export const candidateCompanyHierarchySelectedLogicSelector = (state) =>
	state.filter.candidate_filters.company_hierarchy_filter_mode;

export const companySpecialtiesOrSelector = (state) => state.filter.company_filters.specialties_or;

export const companySpecialtiesAndSelector = (state) =>
	state.filter.company_filters.specialties_and;

export const companySpecialtiesNotSelector = (state) =>
	state.filter.company_filters.specialties_not;

export const companySpecialitiesFilterKeyByLogic = createSelector(
	(_, logic) => logic,
	(logic) => {
		switch (logic) {
			case "not":
				return "specialties_not";
			case "or":
				return "specialties_or";
			case "and":
			default:
				return "specialties_and";
		}
	},
);

export const specialtiesByLogicSelector = createSelector(
	[
		companySpecialtiesAndSelector,
		companySpecialtiesOrSelector,
		companySpecialtiesNotSelector,
		(_, logic) => logic,
	],
	(andSpecialties, orSpecialties, notSpecialties, logic) => {
		switch (logic) {
			case "and":
				return andSpecialties || [];
			case "or":
				return orSpecialties || [];
			case "not":
				return notSpecialties || [];
			default:
				return [];
		}
	},
);

export const specialtiesByCurrentLogicSelector = createSelector(
	[
		companySpecialtiesAndSelector,
		companySpecialtiesOrSelector,
		companySpecialtiesNotSelector,
		companySpecialitiesConnectivityLogicSelector,
	],
	(andSpecialties, orSpecialties, notSpecialties, logic) => {
		switch (logic) {
			case "and":
				return andSpecialties || [];
			case "or":
				return orSpecialties || [];
			case "not":
				return notSpecialties || [];
			default:
				return [];
		}
	},
);

export const allSelectedSpecialtiesSelector = createSelector(
	[companySpecialtiesAndSelector, companySpecialtiesOrSelector, companySpecialtiesNotSelector],
	(andSpecialties, orSpecialties, notSpecialties) => {
		return removeDuplicatesFromArray([...andSpecialties, ...orSpecialties, ...notSpecialties]);
	},
);

export const allCompanySpecialtiesCountSelector = createSelector(
	[companySpecialtiesAndSelector, companySpecialtiesOrSelector, companySpecialtiesNotSelector],
	(and, or, not) => (and?.length || 0) + (or?.length || 0) + (not?.length || 0) || 0,
);

// Advanced filter selected-value selectors
export const companyAssetIntensitySelector = (state) =>
	state.filter.company_filters.asset_intensity;

export const companyBusinessModelSelector = (state) => state.filter.company_filters.business_model;

export const companyCommercialMotionSelector = (state) =>
	state.filter.company_filters.commercial_motion;

export const companyCompanyStageSelector = (state) => state.filter.company_filters.company_stage;

export const companyCompetitivePositioningSelector = (state) =>
	state.filter.company_filters.competitive_positioning;

export const companyCustomerBaseSelector = (state) => state.filter.company_filters.customer_base;

export const companyDealComplexitySelector = (state) =>
	state.filter.company_filters.deal_complexity;

export const companyOperatingRhythmSelector = (state) =>
	state.filter.company_filters.operating_rhythm;

export const companyOrgComplexitySelector = (state) => state.filter.company_filters.org_complexity;

export const companyOwnershipSelector = (state) => state.filter.company_filters.ownership;

export const companyRegulationSelector = (state) => state.filter.company_filters.regulation;

export const companySectorTagsSelector = (state) => state.filter.company_filters.sector_tags;

export const companySpecialityTagsSelector = (state) =>
	state.filter.company_filters.speciality_tags;

export const companyTechOrientationSelector = (state) =>
	state.filter.company_filters.tech_orientation;

export const filterSectionCountSelector = createSelector(
	[
		selectedCompaniesSelector,
		companySearchRadiusSelector,
		companyIndustriesSelector,
		companySectorTagsSelector,
		companySizesSelector,
		companyCountriesSelector,
		(state) => state.filter.company_filters.headquarters_countries,
		(state) => state.filter.company_filters.text_keywords_and,
		(state) => state.filter.company_filters.text_keywords_or,
		(state) => state.filter.company_filters.text_keywords_not,
	],
	(
		selectedCompanies,
		searchRadius,
		industries,
		sectorTags,
		sizes,
		countries,
		hqCountries,
		kwAnd,
		kwOr,
		kwNot,
	) => {
		const companyCount = searchRadius ? 0 : selectedCompanies.length;
		const keywordsCount = (kwAnd?.length || 0) + (kwOr?.length || 0) + (kwNot?.length || 0);

		return (
			companyCount +
			(industries?.length || 0) +
			(sectorTags?.length || 0) +
			(sizes?.length || 0) +
			(countries?.length || 0) +
			(hqCountries?.length || 0) +
			keywordsCount
		);
	},
);

export const advancedFiltersCountSelector = createSelector(
	[
		companyAssetIntensitySelector,
		companyBusinessModelSelector,
		companyCommercialMotionSelector,
		companyCompanyStageSelector,
		companyCompetitivePositioningSelector,
		companyCustomerBaseSelector,
		companyDealComplexitySelector,
		companyRevenuesSelector,
		companyIndexesSelector,
		companyOperatingRhythmSelector,
		companyOrgComplexitySelector,
		companyOwnershipSelector,
		companyRegulationSelector,
		companyTechOrientationSelector,
	],
	(...filters) => filters.reduce((sum, f) => sum + (f?.length || 0), 0),
);
